import { useCallback, useEffect, useState } from 'react';
import { datadogRum } from '@/lib/DatadogService';

const RECAPTCHA_SCRIPT_ID = 'recaptcha-script';
const RECAPTCHA_LOAD_TIMEOUT = 20_000;
const RECAPTCHA_READY_TIMEOUT = 10_000;

const isRecaptchaExecutable = () => !!window.grecaptcha?.enterprise?.execute;

const logRecaptchaWarning = (
  message: string,
  error?: unknown,
  context?: Record<string, unknown>
) => {
  console.warn(`[reCAPTCHA] ${message}`, error);
  datadogRum.addError(error instanceof Error ? error : new Error(message), {
    component: 'recaptcha',
    message,
    ...context
  });
};

const waitForRecaptchaReady = () =>
  new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      reject(new Error('reCAPTCHA did not become ready within the expected time.'));
    }, RECAPTCHA_READY_TIMEOUT);

    window.grecaptcha?.enterprise?.ready(() => {
      window.clearTimeout(timeoutId);

      if (!isRecaptchaExecutable()) {
        reject(new Error('reCAPTCHA execute is unavailable.'));
        return;
      }

      resolve();
    });
  });

const useRecaptcha = () => {
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
    const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      logRecaptchaWarning('Site key is not configured.');
      return;
    }

    if (isRecaptchaExecutable()) {
      setRecaptchaLoaded(true);
      return;
    }

    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `https://recaptcha.net/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.onload = () => {
        if (isRecaptchaExecutable()) {
          setRecaptchaLoaded(true);
        }
      };
      script.onerror = error => {
        logRecaptchaWarning('Failed to load the enterprise script.', error);
      };
      document.body.appendChild(script);
    }

    const timeoutId = window.setTimeout(() => {
      if (!isRecaptchaExecutable()) {
        logRecaptchaWarning('Failed to become executable within the expected time.');
      }
    }, RECAPTCHA_LOAD_TIMEOUT);
    const intervalId = window.setInterval(() => {
      if (isRecaptchaExecutable()) {
        setRecaptchaLoaded(true);
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
      }
    }, 250);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  const executeRecaptcha = useCallback(async (action: string) => {
    try {
      const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        throw new Error('reCAPTCHA site key is not configured.');
      }

      if (!window.grecaptcha?.enterprise?.ready) {
        throw new Error('reCAPTCHA is not available.');
      }

      await waitForRecaptchaReady();
      return await window.grecaptcha.enterprise.execute(siteKey, { action });
    } catch (error) {
      logRecaptchaWarning(`Failed to execute action "${action}".`, error, { action });
      throw error;
    }
  }, []);

  return {
    executeRecaptcha,
    recaptchaLoaded
  };
};

export default useRecaptcha;
